import {
    Button,
    Card,
    CardBody,
    Dialog,
    DialogBody,
    DialogFooter,
    DialogHeader,
    IconButton,
    Input,
    Option,
    Select,
    Typography,
} from "@material-tailwind/react";
import {
    CheckCircleIcon,
    MagnifyingGlassIcon,
    PlusCircleIcon,
    ShieldCheckIcon,
    UserGroupIcon,
    XCircleIcon,
    XMarkIcon,
} from "@heroicons/react/24/outline";
import React, {useCallback, useEffect, useMemo, useState} from "react";
import axios from "axios";
import getData from "../../api/axiosInstance";
import {useTheme} from "../common/ThemeContext";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:8000/arcado_admin";
const ASSIGNABLE_ROLES = ["USER", "WORKER"];

const roleBadgeClass = (role) => {
    if (role === "ADMIN") {
        return "border-panda-pink/40 bg-panda-pink/10 text-panda-pink";
    }

    if (role === "WORKER") {
        return "border-panda-yellow/40 bg-panda-yellow/10 text-amber-700 dark:text-panda-yellow";
    }

    return "border-panda-cyan/40 bg-panda-cyan/10 text-cyan-900 dark:text-panda-cyan";
};

export default function UsersDashboard() {
    const [users, setUsers] = useState([]);
    const [searchTerm, setSearchTerm] = useState("");
    const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
    const [newUserData, setNewUserData] = useState({
        username: "",
        password: "",
        role: "USER",
    });
    const [formError, setFormError] = useState("");
    const {isDark} = useTheme();

    const token = localStorage.getItem("token");
    const requestHeaders = useMemo(() => {
        return {
            Authorization: `Bearer ${token}`,
        };
    }, [token]);

    const filteredUsers = users.filter((user) => {
        const queryWords = searchTerm.toLowerCase().split(" ").filter(Boolean);
        const searchableText = [
            user.id,
            user.username,
            user.role,
            ...(user.authorities || []),
        ].join(" ").toLowerCase();

        return queryWords.every((word) => searchableText.includes(word));
    });

    const refreshUsers = useCallback(() => {
        getData(
            "/users/all",
            requestHeaders,
            setUsers,
            "Error fetching users data"
        );
    }, [requestHeaders]);

    useEffect(() => {
        refreshUsers();
    }, [refreshUsers]);

    const toggleAddDialog = () => {
        setIsAddDialogOpen((current) => !current);
        setFormError("");
    };

    const resetForm = () => {
        setNewUserData({
            username: "",
            password: "",
            role: "USER",
        });
        setFormError("");
    };

    const handleInput = (event) => {
        const {name, value} = event.target;
        setNewUserData((previous) => ({
            ...previous,
            [name]: value,
        }));
    };

    const handleCloseDialog = () => {
        resetForm();
        setIsAddDialogOpen(false);
    };

    const handleAddUser = async (event) => {
        event.preventDefault();
        setFormError("");

        try {
            await axios.post(`${API_URL}/users/add`, newUserData, {headers: requestHeaders});
            refreshUsers();
            handleCloseDialog();
        } catch (error) {
            const message = error.response?.data || "Error adding user.";
            setFormError(message);
        }
    };

    return (
        <Card className="w-full shadow-[0_0_15px_rgba(0,0,0,0.1)] relative overflow-hidden dark:bg-dark-900">
            <CardBody className="relative">
                <Typography variant="h5" color="blue-gray" className="mb-4 flex items-center justify-between dark:text-gray-300">
                    Users
                    <Button onClick={toggleAddDialog} size="md" className="mv-primary-button">
                        <PlusCircleIcon className="h-4 w-4"/>
                        Add
                    </Button>
                </Typography>
                <hr className="my-2 border-blue-gray-50 dark:border-gray-700"/>

                <div className="grid grid-cols-1 gap-4 md:grid-cols-3 mb-5">
                    <div className="rounded-lg border border-blue-gray-50 bg-white p-4 dark:border-dark-700 dark:bg-dark-850">
                        <Typography className="text-xs font-medium uppercase text-gray-500 dark:text-gray-400">Total users</Typography>
                        <Typography className="mt-2 text-2xl font-bold text-cyan-950 dark:text-gray-100">{users.length}</Typography>
                    </div>
                    <div className="rounded-lg border border-blue-gray-50 bg-white p-4 dark:border-dark-700 dark:bg-dark-850">
                        <Typography className="text-xs font-medium uppercase text-gray-500 dark:text-gray-400">Workers</Typography>
                        <Typography className="mt-2 text-2xl font-bold text-cyan-950 dark:text-gray-100">
                            {users.filter((user) => user.role === "WORKER").length}
                        </Typography>
                    </div>
                    <div className="rounded-lg border border-blue-gray-50 bg-white p-4 dark:border-dark-700 dark:bg-dark-850">
                        <Typography className="text-xs font-medium uppercase text-gray-500 dark:text-gray-400">Admins</Typography>
                        <Typography className="mt-2 text-2xl font-bold text-cyan-950 dark:text-gray-100">
                            {users.filter((user) => user.role === "ADMIN").length}
                        </Typography>
                    </div>
                </div>

                <div className="relative overflow-x-auto h-[calc(100vh-28rem)] min-h-[22rem] scrollbar-thin scrollbar-thumb-gray-400 scrollbar-track-gray-200 dark:scrollbar-thumb-gray-600 dark:scrollbar-track-dark-800">
                    <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                        <thead className="sticky top-0 bg-gray-50 z-10 dark:bg-dark-800">
                        <tr>
                            <th className="px-6 py-3 text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider text-left border-b border-gray-200 dark:border-gray-700 min-w-[90px]">
                                ID
                            </th>
                            <th className="px-6 py-3 text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider text-left border-b border-gray-200 dark:border-gray-700 min-w-[220px]">
                                Username
                            </th>
                            <th className="px-6 py-3 text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider text-left border-b border-gray-200 dark:border-gray-700 min-w-[160px]">
                                Role
                            </th>
                            <th className="px-6 py-3 text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider text-left border-b border-gray-200 dark:border-gray-700 min-w-[220px]">
                                Authorities
                            </th>
                            <th className="px-6 py-3 text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider text-left border-b border-gray-200 dark:border-gray-700 min-w-[160px]">
                                Status
                            </th>
                        </tr>
                        </thead>
                        <tbody className="bg-white divide-y dark:bg-dark-900 divide-gray-300 dark:divide-gray-700">
                        {filteredUsers.map((user) => (
                            <tr key={user.id}>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-500">
                                    {user.id}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                    <div className="flex items-center gap-3">
                                        <div className="flex h-9 w-9 items-center justify-center rounded-lg border border-cyan-100 bg-cyan-50 text-cyan-900 dark:border-panda-cyan/20 dark:bg-panda-cyan/10 dark:text-panda-cyan">
                                            <UserGroupIcon className="h-5 w-5"/>
                                        </div>
                                        <Typography className="text-sm font-bold text-gray-900 dark:text-gray-200">
                                            {user.username}
                                        </Typography>
                                    </div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                    <span className={`inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1 text-xs font-bold ${roleBadgeClass(user.role)}`}>
                                        <ShieldCheckIcon className="h-3.5 w-3.5"/>
                                        {user.role || "No role"}
                                    </span>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                                    {(user.authorities || []).join(", ") || "/"}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                    {user.enabled && user.accountNonLocked ? (
                                        <span className="inline-flex items-center gap-1.5 text-sm font-medium text-green-700 dark:text-panda-green">
                                            <CheckCircleIcon className="h-4 w-4"/>
                                            Active
                                        </span>
                                    ) : (
                                        <span className="inline-flex items-center gap-1.5 text-sm font-medium text-red-700 dark:text-panda-pink">
                                            <XCircleIcon className="h-4 w-4"/>
                                            Disabled
                                        </span>
                                    )}
                                </td>
                            </tr>
                        ))}
                        </tbody>
                    </table>
                </div>

                <div className="flex items-center mt-5 w-full gap-2 md:w-max">
                    <div className="relative">
                        <Input
                            type="search"
                            placeholder="Search users"
                            containerProps={{
                                className: "min-w-full sm:min-w-[288px] dark:border-gray-600 dark:focus-within:border-gray-500",
                            }}
                            className="!border-t-blue-gray-300 pl-9 placeholder:text-blue-gray-300 focus:!border-blue-gray-300 dark:bg-dark-800 dark:text-white dark:placeholder:text-gray-400 dark:border-gray-600 dark:focus:border-gray-500"
                            labelProps={{
                                className: "before:content-none after:content-none",
                            }}
                            onChange={(event) => setSearchTerm(event.target.value)}
                            color={isDark ? "white" : "gray"}
                        />
                        <MagnifyingGlassIcon className="!absolute left-3 top-[13px] h-4 w-4 text-blue-gray-300 dark:text-gray-400"/>
                    </div>
                    <Button size="md" className="mv-primary-button">
                        <MagnifyingGlassIcon className="h-4 w-4"/>
                        Search
                    </Button>
                </div>

                <Dialog size="sm" open={isAddDialogOpen} handler={handleCloseDialog} className="p-4 mt-10 mb-10 dark:bg-dark-900 dark:border-1 dark:border-gray-800">
                    <DialogHeader className="relative m-0 block">
                        <Typography variant="h4" color="blue-gray" className="dark:text-gray-300">
                            Add user
                        </Typography>
                        <Typography className="mt-1 font-normal text-gray-600 dark:text-gray-400">
                            Create an application user.
                        </Typography>
                        <IconButton
                            size="sm"
                            variant="text"
                            className="!absolute right-3.5 top-3.5"
                            onClick={handleCloseDialog}
                        >
                            <XMarkIcon className="h-4 w-4 stroke-2 dark:text-gray-600 dark:hover:text-gray-500"/>
                        </IconButton>
                    </DialogHeader>
                    <form onSubmit={handleAddUser}>
                        <DialogBody className="space-y-4 pb-6">
                            {formError && (
                                <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-panda-pink/30 dark:bg-panda-pink/10 dark:text-panda-pink">
                                    {formError}
                                </div>
                            )}
                            <div>
                                <Typography variant="small" color="blue-gray" className="mb-2 text-left font-bold dark:text-gray-300">
                                    Username
                                </Typography>
                                <Input
                                    color="gray"
                                    size="lg"
                                    placeholder="eg. nikola"
                                    name="username"
                                    value={newUserData.username}
                                    onChange={handleInput}
                                    className="placeholder:opacity-100 focus:!border-t-gray-900 dark:bg-dark-850"
                                    containerProps={{className: "!min-w-full"}}
                                    labelProps={{className: "hidden"}}
                                />
                            </div>
                            <div>
                                <Typography variant="small" color="blue-gray" className="mb-2 text-left font-bold dark:text-gray-300">
                                    Password
                                </Typography>
                                <Input
                                    type="password"
                                    color="gray"
                                    size="lg"
                                    placeholder="Set temporary password"
                                    name="password"
                                    value={newUserData.password}
                                    onChange={handleInput}
                                    className="placeholder:opacity-100 focus:!border-t-gray-900 dark:bg-dark-850"
                                    containerProps={{className: "!min-w-full"}}
                                    labelProps={{className: "hidden"}}
                                />
                            </div>
                            <div>
                                <Typography variant="small" color="blue-gray" className="mb-2 text-left font-bold dark:text-gray-300">
                                    Role
                                </Typography>
                                <Select
                                    name="role"
                                    value={newUserData.role}
                                    className="!w-full !border-[1.5px] !border-blue-gray-200/90 !border-t-blue-gray-200/90 bg-white dark:bg-dark-850 text-gray-800 ring-4 ring-transparent placeholder:text-gray-600 focus:!border-primary focus:!border-t-blue-gray-900 group-hover:!border-primary"
                                    labelProps={{className: "hidden"}}
                                    onChange={(value) => handleInput({target: {name: "role", value}})}
                                >
                                    {ASSIGNABLE_ROLES.map((role) => (
                                        <Option key={role} value={role}>{role}</Option>
                                    ))}
                                </Select>
                            </div>
                        </DialogBody>
                        <DialogFooter>
                            <Button type="submit" className="mv-primary-button ml-auto">
                                <PlusCircleIcon className="h-4 w-4"/>
                                Add User
                            </Button>
                        </DialogFooter>
                    </form>
                </Dialog>
            </CardBody>
        </Card>
    );
}
